import {
  Text,
  useSitecoreContext,
  LayoutServicePageState,
  Placeholder,
  Field,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { useI18n } from 'next-localization';
import classNames from 'classnames/bind';
import React, { useEffect, useState } from 'react';

import compose from 'lib/enhancers/compose';
import withStyles from 'lib/enhancers/withStyles';
import { dataFetcher } from 'lib/data-fetcher';

import styles from './EventRegistrationPage.module.scss';
import mockData from '../EventDetail/MockData/mock.data.event-details.json';
import { EventDetailResponse, EventOccurrence } from '../EventDetail/Types/EventDetailResponse.Type';

import { useRouter } from 'next/router';
import Loader, { LoaderVariant } from 'components/helpers/Loader/Loader';
import Link from 'components/helpers/Link/Link';
import Icon from 'components/helpers/Icon';

import { getCleanID, getQueryStringParameter, isValidSitecoreItemGUID } from 'lib/utils';
import { ComponentProps } from 'lib/component-props';
import RichTextWrapper from 'components/helpers/RichText/RichTextWrapper';
import withValidDatasourceCheck from 'lib/enhancers/withValidDatasourceCheck';

type EventRegistrationPageProps = ComponentProps & {
  fields: {
    formIntroductionText: Field<string>;
  };
};

const cx = classNames.bind(styles);

const EventRegistrationPage = (props: EventRegistrationPageProps): React.JSX.Element => {
  const { className, rendering } = props;

  const [events, setEvents] = useState<EventDetailResponse[]>([]);
  const [selectedOccurrence, setSelectedOccurrence] = useState<EventOccurrence>();
  const [showNoResultsFound, setShowNoResultsFound] = useState<boolean>(false);
  const [showLoader, setShowLoader] = useState<boolean>(true);

  const router = useRouter();
  const { t } = useI18n();
  const { sitecoreContext } = useSitecoreContext();

  useEffect(() => {
    let eventItemId = '';
    let occurrenceId = '';
    let allParamsFound = false;

    if (sitecoreContext.pageState === LayoutServicePageState.Normal) {
      if (router.isReady) {
        if (router.asPath?.indexOf('?') > 0) {
          const eventGUID = getQueryStringParameter('eventId', router.query);
          const occurrenceGUID = getQueryStringParameter('occurrenceId', router.query);

          isValidSitecoreItemGUID(eventGUID.trim()) && (eventItemId = eventGUID);
          isValidSitecoreItemGUID(occurrenceGUID.trim()) && (occurrenceId = getCleanID(occurrenceGUID));

          if (eventItemId && occurrenceId) {
            allParamsFound = true;
          }
        }
      }

      const baseUrl = process.env['PUBLIC_URL'] || '';
      if (allParamsFound) {
        dataFetcher<EventDetailResponse[]>(`${baseUrl}/api/events/getEventDetail`, {
          eventItemId: eventItemId,
          language: sitecoreContext.language,
        }).then((response) => {
          const $data = response.data;

          setEvents([...new Set($data)]);
          const hasOnDemand =
            $data[0]?.locationType?.targetItems?.some((item) => item.id === '72E8405490274AA386596430EAF72C68') ===
            true;
          if (!hasOnDemand) {
            const eventOccurrences = $data[0]?.children?.EventOccurrences;

            const matchedOccurrence = eventOccurrences?.find((Occurrence) => Occurrence.id === occurrenceId);
            if (!matchedOccurrence) {
              setShowNoResultsFound(true);
            } else {
              setSelectedOccurrence(matchedOccurrence);
              setShowNoResultsFound($data.length === 0);
            }
          } else {
            setShowNoResultsFound(true);
          }
          setShowLoader(false);
        });
      } else {
        setShowNoResultsFound(true);
        setShowLoader(false);
      }
    } else {
      setEvents(mockData as unknown as EventDetailResponse[]);
      setShowLoader(false);
      setShowNoResultsFound((mockData as unknown as EventDetailResponse[]).length === 0);
    }
  }, [router, sitecoreContext]);

  const getEventStartDate = (startDateTime: string, includeYear: boolean) => {
    const StartDate = new Date(startDateTime);
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    };
    if (includeYear) options.year = 'numeric';
    return StartDate.toLocaleDateString(sitecoreContext.language, options);
  };

  const getEventTime = (dateTime: string) => {
    if (dateTime != '0001-01-01T00:00:00Z') {
      const eventDate = new Date(dateTime);
      const eventTime = eventDate.toLocaleTimeString(sitecoreContext.language, {
        hour: 'numeric',
        minute: '2-digit',
      });
      return eventTime.toLowerCase().replace('am', 'a.m.').replace('pm', 'p.m.');
    }
    return;
  };

  return (
    <div className={cx('event-registration-page', 'form-side-margin', className)}>
      {showLoader && (
        <div role="status">
          <span className={cx('screen-reader-only')}>{t('Event Registration Page Loaded')}</span>
          <Loader variant={LoaderVariant.Spinner} />
        </div>
      )}
      {!showLoader && showNoResultsFound && (
        <section role="status" className={cx('event-header')}>
          <div className={cx('no-event-info')}>
            <Icon variant="error" />
            {t('EventRegistrationUnavailable')}
          </div>
        </section>
      )}
      {!showLoader && !showNoResultsFound && events?.length > 0 && (
        <>
          {events.map((event, index) => {
            return (
              <React.Fragment key={index}>
                <section className={cx('event-header')}>
                  <Text tag="h1" field={event.name} encode={sitecoreContext.pageEditing} />
                  <RichTextWrapper field={props.fields.formIntroductionText} />
                </section>
                <>
                  <section className={cx('occurrence-listing')}>
                    <h2>{t('EventDetails')}</h2>
                    <>
                      <section className={cx('occurrence-details')}>
                        {selectedOccurrence?.canceled?.boolValue && (
                          <div className={cx('no-event-info')}>
                            <Icon variant="error" />
                            {t('CanceledText')}
                          </div>
                        )}
                        {selectedOccurrence && (
                          <>
                            <div className={cx('icon-wrapper', 'no-mrgn')}>
                              <Icon variant="calendar" className={cx('event-icon')} /> <span>{t('DateTime')} </span>
                            </div>
                            {selectedOccurrence?.startDateTime?.jsonValue?.value && (
                              <span className={cx('event-date', 'occurrence-info')}>
                                {getEventStartDate(selectedOccurrence?.startDateTime?.jsonValue?.value, true)}
                              </span>
                            )}
                            <div className={cx('occurrence-info')}>
                              {getEventTime(selectedOccurrence?.startDateTime?.jsonValue?.value)}
                              {selectedOccurrence?.endDateTime?.jsonValue?.value != '0001-01-01T00:00:00Z' && (
                                <> - {getEventTime(selectedOccurrence?.endDateTime?.jsonValue?.value)}</>
                              )}
                            </div>
                          </>
                        )}
                        {event.inPersonEventLocation?.targetItem && (
                          <>
                            <div className={cx('icon-wrapper')}>
                              <Icon variant="location" className={cx('event-icon')} /> <span>{t('Location')}</span>
                            </div>
                            <div className={cx('occurrence-info')}>
                              <Text
                                tag="div"
                                field={event.inPersonEventLocation?.targetItem?.locationName}
                                encode={sitecoreContext.pageEditing}
                              />
                              <Text
                                tag="div"
                                field={event.inPersonEventLocation?.targetItem?.addressLine1}
                                encode={sitecoreContext.pageEditing}
                              />
                              {event.inPersonEventLocation?.targetItem?.addressLine2?.value && (
                                <Text
                                  tag="div"
                                  field={event.inPersonEventLocation?.targetItem?.addressLine2}
                                  encode={sitecoreContext.pageEditing}
                                />
                              )}
                              <div>
                                {event.inPersonEventLocation?.targetItem?.city?.value}
                                {event.inPersonEventLocation?.targetItem?.state?.value && ', '}
                                {event.inPersonEventLocation?.targetItem?.state?.value}
                                {event.inPersonEventLocation?.targetItem?.zipCode?.value && ' '}
                                {event.inPersonEventLocation?.targetItem?.zipCode?.value}
                              </div>
                              <Link
                                field={event.inPersonEventLocation?.targetItem?.directionsLink?.jsonValue}
                                icon="external-link"
                                showLinkTextWithChildrenPresent={false}
                                target={
                                  event.inPersonEventLocation?.targetItem?.directionsLink?.jsonValue?.value?.target ||
                                  '_blank'
                                }
                              >
                                {t('GetDirections')}
                              </Link>
                            </div>
                          </>
                        )}

                        {event.onlineEventSpecialInstructions.value && (
                          <>
                            <div className={cx('icon-wrapper')}>
                              <Icon variant="video" className={cx('event-icon')} /> <span>{t('OnlineEvent')} </span>
                            </div>
                            <Text
                              tag="p"
                              field={event.onlineEventSpecialInstructions}
                              className={cx('occurrence-info')}
                            />
                          </>
                        )}
                        {event.host?.targetItem && (
                          <>
                            <div className={cx('icon-wrapper')}>
                              <Icon variant="person" className={cx('event-icon')} /> <span>{t('HostedBy')} </span>
                            </div>

                            <Link
                              field={event.host?.targetItem?.hostUrl?.jsonValue}
                              showLinkTextWithChildrenPresent={false}
                              target={event.host?.targetItem?.hostUrl?.jsonValue?.value?.target || '_blank'}
                              className={cx('occurrence-info')}
                            >
                              <Text field={event.host?.targetItem?.hostName} encode={sitecoreContext.pageEditing} />
                            </Link>
                          </>
                        )}
                      </section>
                    </>
                  </section>
                </>
              </React.Fragment>
            );
          })}
        </>
      )}
      <>
        {selectedOccurrence?.canceled?.boolValue && (
          <div className={cx('no-event-info')}>
            <Icon variant="error" />
            {t('CanceledText')}
          </div>
        )}

        <section
          className={cx('event-registration-form', {
            'event-registration-form--hidden':
              !showLoader && (showNoResultsFound || selectedOccurrence?.canceled?.boolValue),
          })}
        >
          <Placeholder key={0} name="hp-container-3070-left" rendering={rendering} disableSuspense={true} />
        </section>
      </>
    </div>
  );
};

export default compose<EventRegistrationPageProps>(withValidDatasourceCheck(), withStyles())(EventRegistrationPage);
